import { html, nothing, type TemplateResult } from "lit-html";

import { api } from "../api.js";
import { bootstrapGenesisLocal, persistUnlocked } from "../client-crypto.js";
import { setRoute, setUser, showToast, setLoading, state as appState } from "../main.js";

let state = {
  email: "",
  personal_email: "",
  name: "",
  password: "",
  password2: "",
  busy: false,
  err: "",
  recoveryCodes: [] as string[],
};

async function submit(): Promise<void> {
  if (state.busy) return;
  if (state.password !== state.password2) {
    state.err = appState.lang === "en" ? "Passwords do not match" : "パスワードが一致しません";
    return;
  }
  if (state.password.length < 12) {
    state.err = appState.lang === "en" ? "Password must be at least 12 chars" : "パスワードは 12 文字以上必要です";
    return;
  }
  state.busy = true; state.err = ""; setLoading(true);
  try {
    const boot = await bootstrapGenesisLocal({
      email: state.email,
      personal_email: state.personal_email,
      name: state.name,
      password: state.password,
    });
    const resp = await api.genesis(boot.body);
    state.recoveryCodes = boot.recoveryCodesPlain;
    (window as unknown as { __unlocked?: unknown }).__unlocked = boot.unlocked;
    persistUnlocked(boot.unlocked);
    setUser({ id: resp.user_id, email: resp.email, name: state.name, role: "SUPER_USER" });
    showToast(appState.lang === "en"
      ? "Super User created. Save your recovery codes."
      : "Super User を作成しました。リカバリコードを保管してください。");
  } catch (err) {
    state.err = (err as Error).message;
  } finally {
    state.busy = false;
    setLoading(false);
  }
}

export function renderGenesis(): TemplateResult {
  if (state.recoveryCodes.length > 0) {
    return html`
      <div class="login-shell">
        <div class="login-card" style="max-width: 520px;">
          <h1>
            <span data-lang="en">Recovery codes</span><span data-lang="jp">リカバリコード</span>
          </h1>
          <p style="color: var(--text-2); font-size: 13px;">
            <span data-lang="en">Save these 10 codes somewhere safe (paper, password manager). Each can be used once if you lose your 2FA authenticator.</span><span data-lang="jp">この 10 個のコードを安全な場所 (紙、パスワードマネージャ等) に保管してください。2FA 認証アプリを失った場合、各コードは 1 回ずつ使用できます。</span>
          </p>
          <pre style="background: var(--surface-2); padding: 16px; border-radius: 8px; font-family: ui-monospace, Consolas, monospace; font-size: 14px; line-height: 1.8;">${state.recoveryCodes.join("\n")}</pre>
          <button class="primary" style="width: 100%; margin-top: 16px;"
            @click=${() => { state.recoveryCodes = []; setRoute("vault"); }}>
            <span data-lang="en">I have saved them, continue</span><span data-lang="jp">保管しました。次へ</span>
          </button>
        </div>
      </div>
    `;
  }
  return html`
    <div class="login-shell">
      <div class="login-card">
        <h1>
          <span data-lang="en">First setup</span><span data-lang="jp">初期設定</span>
        </h1>
        <p style="color: var(--text-2); font-size: 13px; margin-bottom: 16px;">
          <span data-lang="en">Creates the Super User. This can only happen once.</span><span data-lang="jp">Super User を作成します。一度だけ実行できます。</span>
        </p>
        <div class="form-row">
          <label>
            <span data-lang="en">Full name</span><span data-lang="jp">氏名</span>
          </label>
          <input type="text" .value=${state.name}
            @input=${(e: InputEvent) => (state.name = (e.target as HTMLInputElement).value)} />
        </div>
        <div class="form-row">
          <label>
            <span data-lang="en">Email</span><span data-lang="jp">メールアドレス</span>
          </label>
          <input type="email" .value=${state.email}
            @input=${(e: InputEvent) => (state.email = (e.target as HTMLInputElement).value)} />
        </div>
        <div class="form-row">
          <label>
            <span data-lang="en">Recovery email <span style="color: var(--text-2); font-weight: 400;">(different from primary, for password reset)</span></span><span data-lang="jp">リカバリ用メール <span style="color: var(--text-2); font-weight: 400;">(主メールと異なる、パスワードリセット用)</span></span>
          </label>
          <input type="email" .value=${state.personal_email}
            @input=${(e: InputEvent) => (state.personal_email = (e.target as HTMLInputElement).value)} />
        </div>
        <div class="form-row">
          <label>
            <span data-lang="en">Master password (12+ chars)</span><span data-lang="jp">マスターパスワード (12 文字以上)</span>
          </label>
          <input type="password" .value=${state.password}
            @input=${(e: InputEvent) => (state.password = (e.target as HTMLInputElement).value)} />
        </div>
        <div class="form-row">
          <label>
            <span data-lang="en">Confirm password</span><span data-lang="jp">パスワード確認</span>
          </label>
          <input type="password" .value=${state.password2}
            @input=${(e: InputEvent) => (state.password2 = (e.target as HTMLInputElement).value)} />
        </div>
        ${state.err ? html`<p style="color: var(--status-error); font-size: 13px;">${state.err}</p>` : nothing}
        <button class="primary" style="width: 100%; margin-top: 8px;" ?disabled=${state.busy}
          @click=${() => void submit()}>
          ${state.busy
            ? html`<span data-lang="en">Creating…</span><span data-lang="jp">作成中…</span>`
            : html`<span data-lang="en">Create Super User</span><span data-lang="jp">Super User を作成</span>`}
        </button>
      </div>
    </div>
  `;
}
